/**
 * Open Dental imaging-category map for copied / seeded forms (SuperAdmin onboarding).
 *
 * THE RULE (2026-09-14): a copied form's PDF category is chosen purely by its FormType from the
 * TARGET site's own imaging categories (DefNums are per site — dev 136 = "Credit Approval",
 * Schuyler prod 136 = "Consent Forms"). The operator picks one category per type on every run;
 * nothing is saved. NP_FORM / MED_HX_UPDATE may be preselected from the backend's suggestions,
 * OTHER (consents) is ALWAYS asked — never preselected.
 *
 * The backend (teamio-admin form-imaging-defaults.js) is the authority and refuses an incomplete
 * map. This file only keeps the UI from offering a click the backend is guaranteed to refuse.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE. cypress/node-tests/superadmin-forms-imaging-map.test.js loads and
 * executes this exact file (stripping `export`) instead of mirroring it.
 */

export const FORM_IMAGING_TYPES = ['OTHER', 'NP_FORM', 'MED_HX_UPDATE'];

export const FORM_IMAGING_TYPE_LABELS = {
    OTHER: 'Consent & other forms (OTHER)',
    NP_FORM: 'New patient forms (NP_FORM)',
    MED_HX_UPDATE: 'Medical history updates (MED_HX_UPDATE)',
};

export const IMAGING_FIELD_LABELS = {
    pdfFileName: 'File name',
    pdfCategory: 'PDF category',
    attachmentCategory: 'Uploads category',
};

export function emptyCategoryMap() {
    return { OTHER: '', NP_FORM: '', MED_HX_UPDATE: '' };
}

export function isBlankValue(v) {
    return v === null || v === undefined || String(v).trim() === '';
}

/** Blank or unknown FormType counts as OTHER. */
export function normalizeFormType(t) {
    const s = isBlankValue(t) ? '' : String(t).trim().toUpperCase();
    return (s === 'NP_FORM' || s === 'MED_HX_UPDATE') ? s : 'OTHER';
}

/**
 * The types a batch of forms needs mapped, in FORM_IMAGING_TYPES order.
 * A FormType that is not EXACTLY a known type (e.g. ' np_form ') also requires OTHER: whether the
 * backend reads it leniently or strictly, both interpretations are covered, so the UI can never
 * enable a copy the backend refuses for a missing type.
 */
export function requiredTypesForForms(forms) {
    const need = {};
    (forms || []).forEach(f => {
        const raw = f ? f.FormType : undefined;
        need[normalizeFormType(raw)] = true;
        if (raw !== 'NP_FORM' && raw !== 'MED_HX_UPDATE') need.OTHER = true;
    });
    return FORM_IMAGING_TYPES.filter(t => need[t]);
}

/** Selected forms only (by FormIDForWeb). */
export function requiredTypesForSelection(availableForms, selectedFormIDs) {
    const ids = {};
    (selectedFormIDs || []).forEach(id => { ids[String(id)] = true; });
    return requiredTypesForForms((availableForms || []).filter(f => f && ids[String(f.FormIDForWeb)]));
}

function hasCategory(categories, defNum) {
    return (categories || []).some(c => c && String(c.DefNum) === String(defNum).trim());
}

/**
 * Every required type has a non-blank DefNum that exists in the site's category list.
 * No categories ⇒ never complete (the backend refuses a site with no categories).
 */
export function isCategoryMapComplete(map, types, categories) {
    const req = types || FORM_IMAGING_TYPES;
    if (!map || !Array.isArray(categories) || categories.length === 0) return false;
    return req.every(t => !isBlankValue(map[t]) && hasCategory(categories, map[t]));
}

/**
 * Merge the operator's current picks with a (re)loaded category list. "Asked each time" means per SITE
 * VISIT, not per reload, so a same-site reload keeps picks:
 *   1. a picked DefNum that is still in the list is kept (trimmed, as a string);
 *   2. a picked DefNum no longer in the list goes back to '' (it would file PDFs nowhere valid);
 *   3. only BLANK NP_FORM / MED_HX_UPDATE rows are filled from suggestions (a pick is never overridden).
 * OTHER is NEVER auto-filled — it survives only as the operator's own still-valid pick.
 * prevMap null (new site) ⇒ plain suggestion preselect.
 */
export function mergeImagingMap(prevMap, categories, suggestions) {
    const map = emptyCategoryMap();
    const prev = prevMap || {};
    FORM_IMAGING_TYPES.forEach(t => {
        if (!isBlankValue(prev[t]) && hasCategory(categories, prev[t])) map[t] = String(prev[t]).trim();
    });
    const s = suggestions || {};
    ['NP_FORM', 'MED_HX_UPDATE'].forEach(t => {
        const hit = s[t];
        if (map[t] === '' && hit && !isBlankValue(hit.DefNum) && hasCategory(categories, hit.DefNum)) {
            map[t] = String(hit.DefNum).trim();
        }
    });
    return map;
}

/** Preselect from backend suggestions for a fresh site visit. OTHER is NEVER preselected. */
export function mapFromSuggestions(suggestions, categories) {
    return mergeImagingMap(null, categories, suggestions);
}

/** For the OTHER dropdown: consent-like categories first, then the rest (no duplicates). */
export function splitConsentFirst(categories, consentCandidates) {
    const cats = categories || [];
    const wanted = {};
    (consentCandidates || []).forEach(c => { if (c && !isBlankValue(c.DefNum)) wanted[String(c.DefNum)] = true; });
    return {
        first: cats.filter(c => wanted[String(c.DefNum)]),
        rest: cats.filter(c => !wanted[String(c.DefNum)]),
    };
}

export function categoryName(categories, defNum) {
    if (isBlankValue(defNum)) return '';
    const hit = (categories || []).find(c => c && String(c.DefNum) === String(defNum).trim());
    return hit ? hit.ItemName : '';
}

/** Display value for the Apply preview table. */
export function describeImagingValue(field, value, categories) {
    if (isBlankValue(value)) return '(blank)';
    if (field === 'pdfCategory' || field === 'attachmentCategory') {
        return categoryName(categories, value) || String(value);
    }
    return String(value);
}

/** Bulk Wiki Copy missing-forms modal: every form needs a PDF category. Empty config ⇒ false. */
export function perFormPdfCategoriesComplete(perFormConfig) {
    const keys = Object.keys(perFormConfig || {});
    if (keys.length === 0) return false;
    return keys.every(k => perFormConfig[k] && !isBlankValue(perFormConfig[k].pdfCategory));
}
